import { useState } from "react";
import PropTypes from "prop-types";
import {
  FaInfoCircle,
  FaMapMarkedAlt,
  FaSearchLocation,
  FaKey,
  FaBrain,
  FaDatabase,
  FaQuestionCircle,
  FaBars,
  FaChevronLeft,
  FaLock,
  FaUnlock,
  FaGlobeAmericas,
} from "react-icons/fa";
import AuthDialog from "./AuthDialog";
import "./SearchHub.css";

// Custom MenuItem component to replace react-pro-sidebar MenuItem
const MenuItem = ({ icon, children, onClick, className, collapsed }) => {
  const [isHovered, setIsHovered] = useState(false);

  const menuText =
    typeof children === "object" ? children.props.children[0] : children;

  return (
    <button
      type="button"
      className={`ps-menuitem-root ${className || ""}`}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={menuText}
    >
      <div
        className={`ps-menu-button ${isHovered && collapsed ? "hovered" : ""}`}
      >
        <span className="ps-menu-icon">
          {collapsed && isHovered ? (
            <span className="collapsed-text">{menuText}</span>
          ) : (
            icon
          )}
        </span>
        <span className="ps-menu-label">{children}</span>
      </div>
    </button>
  );
};

MenuItem.propTypes = {
  icon: PropTypes.node,
  // May be a string or element; code reads children.props for label text.
  children: PropTypes.any,
  onClick: PropTypes.func,
  className: PropTypes.string,
  collapsed: PropTypes.bool,
};

// Custom Menu component to replace react-pro-sidebar Menu
const Menu = ({ children }) => {
  return <div className="ps-menu-root">{children}</div>;
};

Menu.propTypes = {
  children: PropTypes.node,
};

// Custom Sidebar component to replace react-pro-sidebar Sidebar
const CustomSidebar = ({
  collapsed,
  width,
  collapsedWidth,
  className,
  children,
}) => {
  const style = {
    width: collapsed ? collapsedWidth : width,
  };

  return (
    <div className={`ps-sidebar-container ${className || ""}`} style={style}>
      <div className="ps-sidebar-root">{children}</div>
    </div>
  );
};

CustomSidebar.propTypes = {
  collapsed: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  collapsedWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  children: PropTypes.node,
};

const SearchHub = ({
  onSearch,
  collapsed,
  onCollapse,
  isAuthenticated,
  activeComponent,
}) => {
  const [showAuthDialog, setShowAuthDialog] = useState(false);

  const renderMenuItem = (icon, label, id, requiresAuth = false) => {
    const isActive = activeComponent === id;
    const isLocked = requiresAuth && !isAuthenticated;

    return (
      <MenuItem
        icon={icon}
        onClick={() => {
          if (isLocked) {
            setShowAuthDialog(true);
          } else {
            onSearch(id);
          }
        }}
        className={`${isActive ? "ps-active" : ""} ${isLocked ? "locked" : ""}`}
        collapsed={collapsed}
      >
        <div className="menu-item-content">
          {label}
          {isLocked && (
            <span className="menu-auth-icon">
              <FaLock />
            </span>
          )}
          {!isLocked && isAuthenticated && (
            <span className="menu-auth-icon unlocked">
              <FaUnlock />
            </span>
          )}
        </div>
      </MenuItem>
    );
  };

  return (
    <>
      <CustomSidebar
        collapsed={collapsed}
        width="260px"
        collapsedWidth="80px"
        className={`search-hub-sidebar ${collapsed ? "collapsed" : ""}`}
      >
        <Menu>
          <MenuItem
            icon={collapsed ? <FaBars /> : <FaChevronLeft />}
            onClick={() => onCollapse(!collapsed)}
            className="collapse-button"
            collapsed={collapsed}
          >
            {!collapsed && "Navigation"}
          </MenuItem>

          {renderMenuItem(<FaInfoCircle />, "About", "about")}
          {renderMenuItem(<FaGlobeAmericas />, "Navix", "counties", false)}
          {renderMenuItem(<FaMapMarkedAlt />, "Map", "map", true)}
          {renderMenuItem(<FaSearchLocation />, "Location", "location", true)}
          {renderMenuItem(<FaKey />, "Keyword", "keyword", true)}
          {renderMenuItem(<FaBrain />, "AI", "ai", true)}
          {renderMenuItem(<FaDatabase />, "Datasets", "datasets", true)}
          {renderMenuItem(<FaQuestionCircle />, "Help", "help")}
        </Menu>
      </CustomSidebar>
      <AuthDialog
        isOpen={showAuthDialog}
        onClose={() => setShowAuthDialog(false)}
      />
    </>
  );
};

SearchHub.propTypes = {
  onSearch: PropTypes.func,
  collapsed: PropTypes.bool,
  onCollapse: PropTypes.func,
  isAuthenticated: PropTypes.bool,
  activeComponent: PropTypes.string,
};

export default SearchHub;
